import React, {
	cloneElement,
	useState,
	useEffect,
	isValidElement,
	forwardRef,
	useRef,
	useImperativeHandle
} from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Tippy from '@tippyjs/react'
import useForkRef from '../../utils/useForkRef'
import useControlled from '../../utils/useControlled'
import withTheme from '../styles/withTheme'
import { Icon, HelperText } from '../../index'

import './dropdown.scss'

/* @TODO
 * - keyboard navitagion
 */

function areEqualValues(a, b) {
	if (typeof b === 'object' && b !== null) {
		return a === b
	}
	return String(a) === String(b)
}

const Dropdown = forwardRef(function Dropdown(props, ref) {
	const {
		appendMenuTo = 'parent',
		menuZIndex = 9999,
		children,
		className,
		defaultValue = '',
		disabled,
		dropdownProps,
		helperText,
		icon,
		inputRef: inputRefProp,
		isCorrect: isCorrectProp,
		label,
		labelId,
		name,
		onBlur,
		onChange,
		onClose,
		onFocus,
		onOpen,
		open: openProp,
		readOnly = false,
		required = false,
		showArrow = true,
		tabIndex: tabIndexProp,
		theme,
		value: valueProp,
		...other
	} = props

	//--------------------------------------------------------
	/* Variables */

	const inputRef = useRef(null)
	const [displayNode, setDisplayNode] = useState(null)
	const [openState, setOpenState] = useState(false)
	const [requiredFilled, setRequiredFilled] = useState(null)
	const [valueName, setValueName] = useState('')
	const [value, setValue] = useControlled({
		controlled: valueProp,
		default: defaultValue
	})
	const [isCorrect, setIsCorrect] = useControlled({
		controlled: isCorrectProp
	})
	const { current: isControlled } = useRef(isCorrectProp != null)
	const handleRef = useForkRef(ref, inputRefProp)

	const childrenArray = React.Children.toArray(children)

	useImperativeHandle(
		handleRef,
		() => ({
			focus: () => {
				displayNode.focus()
			},
			node: inputRef.current,
			value
		}),
		[displayNode, value]
	)

	//--------------------------------------------------------
	/* Update open/close state */

	useEffect(() => {
		setOpenState(false)
		if (!value) {
			setValueName('')
		} else {
			const index = childrenArray.map((child) => child.props.value).indexOf(value)

			if (index === -1) {
				return
			}
			const child = childrenArray[index]
			setValueName(child.props.children)
		}
	}, [value])

	const update = (open, event) => {
		if (open) {
			if (onOpen) {
				onOpen(event)
			}
		} else if (onClose) {
			onClose(event)
		}
		setOpenState(open)
	}

	const handleMouseDown = (event) => {
		// Ignore everything but left-click
		if (event.button !== 0) {
			return
		}
		// Hijack the default focus behavior.
		event.preventDefault()
		if (openState) {
			update(false, event)
		} else {
			update(true, event)
		}
	}

	const handleClickOutside = (_, event) => {
		event.stopPropagation()
		setTimeout(() => {
			if (required && value == '') {
				setRequiredFilled(false)
			}
			update(false, event)
		})
	}

	//--------------------------------------------------------
	/* Handle mouse clicks and keyboard access */
	const handleItemClick = (child) => (event) => {
		event.stopPropagation()
		let newValue = child.props.value

		if (child.props.onClick) {
			child.props.onClick(event)
		}
		if (value === newValue) {
			update(false, event)
			return
		}

		setValue(newValue)
		setValueName(child.props.children)

		required !== null && !requiredFilled && setRequiredFilled(true)

		if (onChange) {
			event.persist()
			Object.defineProperty(event, 'target', {
				writable: true,
				value: { value: newValue, description: child.props.children, name }
			})
			onChange(event, child)
		}
		update(false, event)
	}

	const handleBlur = (event) => {
		// if open event.stopImmediatePropagation
		if (openState && onBlur) {
			event.persist()
			// Preact support, target is read only property on a native event.
			Object.defineProperty(event, 'target', {
				writable: true,
				value: { value, name }
			})
			onBlur(event)
		}
	}

	/* @TODO: handle key navigation through options and selection */
	const handleKeyDown = (event) => {
		if (!readOnly) {
			const validKeys = [' ', 'ArrowUp', 'ArrowDown', 'Enter']

			if (validKeys.indexOf(event.key) !== -1) {
				event.preventDefault()
				update(true, event)
			}
		}
	}

	// Support autofill.
	const handleChange = (event) => {
		const index = childrenArray.map((child) => child.props.value).indexOf(value)

		if (index === -1) {
			return
		}

		const child = childrenArray[index]
		setValue(child.props.value)
		setValueName(child.props.children)

		if (onChange) {
			onChange(event, child)
		}
	}

	// If disabled or spicified in props,
	// set keyboard tabulation access to the element
	let tabIndex
	if (typeof tabIndexProp !== 'undefined') {
		tabIndex = tabIndexProp
	} else {
		tabIndex = disabled ? null : 0
	}

	//--------------------------------------------------------
	/* Evaluate arrow-icon color */
	const arrowColor = () => {
		if (disabled) {
			return 'gray-3'
		} else if (isControlled) {
			return !isCorrect ? 'error' : 'primary-lima'
		} else if (requiredFilled !== null && !requiredFilled) {
			return 'error'
		} else {
			return 'primary-lima'
		}
	}

	//--------------------------------------------------------
	/* Generate select options (children) */
	delete other['aria-invalid']

	const items = childrenArray.map((child, key) => {
		if (!isValidElement(child)) {
			return null
		}

		let selected
		selected = areEqualValues(value, child.props.value)

		return cloneElement(child, {
			'aria-selected': selected ? 'true' : undefined,
			onClick: handleItemClick(child),
			onKeyUp: (event) => {
				/* about keyboard navigation, to review in the future */
				// if (event.key === ' ') {
				//     // otherwise our MenuItems dispatches a click event
				//     // it's not behavior of the native <option> and causes
				//     // the select to close immediately since we open on space keydown
				//     event.preventDefault()
				// }

				if (child.props.onKeyUp) {
					child.props.onKeyUp(event)
				}
			},
			role: 'li',
			selected,
			value: undefined, // The value is most likely not a valid HTML attribute.
			'data-value': child.props.value, // Instead, we provide it as a data attribute.
			key
		})
	})

	//--------------------------------------------------------
	/* Render */
	return (
		<div ref={ref} className={clsx('Vb-dropdown-wrapper', helperText && 'Vb-dropdown--has-helpertext')}>
			<div>
				<Tippy
					appendTo={appendMenuTo}
					content={<ul>{items}</ul>}
					theme={`Vb-dropdown--theme-${theme}`}
					className={clsx('Vb-dropdown__items-list')}
					visible={openState}
					delay={0}
					duration={0}
					placement="bottom"
					interactive
					arrow={false}
					offset={0}
					maxWidth="none"
					onClickOutside={handleClickOutside}
					zIndex={menuZIndex}
					popperOptions={{
						modifiers: [
							{
								name: "sameWidth",
								enabled: true,
								phase: "beforeWrite",
								requires: ["computeStyles"],
								fn: ({ state }) => {
									state.styles.popper.width = `${state.rects.reference.width}px`
								},
								effect: ({ state }) => {
									state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`
								}
							}
						]
					}}
				>
					<div
						className={clsx(
							'Vb-dropdown',
							label && 'Vb-dropdown--with-label',
							valueName && 'Vb-dropdown--has-value',
							openState && 'Vb-dropdown--focus',
							isCorrect === false && 'Vb-dropdown--error',
							icon && 'Vb-dropdown--with-icon',
							className
						)}
						ref={setDisplayNode}
						role="button"
						aria-disabled={disabled ? 'true' : undefined}
						aria-expanded={openState ? 'true' : undefined}
						aria-labelledby={labelId && labelId}
						onMouseDown={disabled || readOnly ? null : handleMouseDown}
						onKeyDown={handleKeyDown}
						onBlur={handleBlur}
						onFocus={onFocus}
						tabIndex={tabIndex}
						{...dropdownProps}
					>
						{icon && (
							<div className="Vb-dropdown__icon">
								<Icon size={22} sizeInPx color="gray-2">
									{icon}
								</Icon>
							</div>
						)}
						{label && <label className="Vb-dropdown__label">{label}</label>}
						<div className="Vb-dropdown__selection">{valueName}</div>
						<input
							className="Vb-dropdown__native-input"
							aria-hidden
							tabIndex={-1}
							onChange={handleChange}
							ref={inputRef}
							name={name}
							readOnly
							required={required}
							value={value}
							{...other}
						/>

						{showArrow && (
							<div className="Vb-dropdown__arrow">
								<Icon color={arrowColor()} size={1}>
									{openState ? 'arrow-up' : 'arrow-down'}
								</Icon>
							</div>
						)}
					</div>
				</Tippy>
			</div>
			{helperText && <HelperText isError={isCorrect !== null && !isCorrect}>{helperText}</HelperText>}
		</div>
	)
})

export default withTheme('Vb-dropdown')(Dropdown)

Dropdown.propTypes = {
	/**
	 * The element to append the menu to. Sometimes the menu needs to be appended to a different DOM context due to clipping, or z-index issues.
	 */
	appendMenuTo: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.any }),
		PropTypes.node
	]),
	/**
	 * Modal children, usually the included sub-components.
	 */
	children: PropTypes.node,
	/**
	 * The CSS class name of the dropdown element.
	 */
	className: PropTypes.string,
	/**
	 * The default element value.
	 * Use when the component is not controlled.
	 */
	defaultValue: PropTypes.any,
	/**
	 * If `true`, the select will be disabled.
	 */
	disabled: PropTypes.bool,
	/**
	 * The text of the HelperText element,
	 * that displays a helper text below the Dropdown.
	 */
	helperText: PropTypes.string,
	/**
	 * Text code from the icon list that the Icon component uses
	 * If setted, an icon will be displayed in the left side of the input
	 */
	icon: PropTypes.string,
	/**
	 * Flag to store the validation of the input
	 * and it's resulting styling
	 */
	isCorrect: PropTypes.bool,
	/**
	 * The label associated to the dropdown input
	 */
	label: PropTypes.string,
	/**
	 * The ID of an element that acts as an additional label.
	 * The Dropdown will be labelled by the additional label and the selected value.
	 */
	labelId: PropTypes.string,
	/**
	 * Callback function fired after a dropdown item is selected.
	 *
	 * @param {object} event The event source of the callback.
	 * You can pull out the new value by accessing `event.target.value` (any).
	 * @param {object} [child] The react element that was selected.
	 */
	onBlur: PropTypes.func,
	/**
	 * Callback function fired when a dropdown item is selected.
	 *
	 * @param {object} event The event source of the callback.
	 * You can pull out the new value by accessing `event.target.value` (any).
	 * @param {object} [child] The react element that was selected.
	 */
	onChange: PropTypes.func,
	/**
	 * Callback fired when the component requests to be closed.
	 * Use in controlled mode (see open).
	 *
	 * @param {object} event The event source of the callback.
	 */
	onClose: PropTypes.func,
	/**
	 * @ignore
	 */
	onFocus: PropTypes.func,
	/**
	 * Callback fired when the component requests to be opened.
	 * Use in controlled mode (see open).
	 *
	 * @param {object} event The event source of the callback.
	 */
	onOpen: PropTypes.func,
	/**
	 * Control `select` open state.
	 */
	open: PropTypes.bool,
	/**
	 * @ignore
	 */
	readOnly: PropTypes.bool,
	/**
	 * If 'false' the Dropdown will keep an empty item to allow not to select anything
	 */
	required: PropTypes.bool,
	/**
	 * @ignore
	 */
	showArrow: PropTypes.bool,
	/**
	 * @ignore
	 */
	tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	/**
	 * The input value.
	 */
	value: PropTypes.any
}
